import React from "react";
import PropTypes from "prop-types";

import { Button, ButtonLink } from "../actions";
import { assertNonEmptyString, assertOneOf } from "../../internal/contracts";
import { useThemeA } from "../../internal/ThemeAContext";
import { FEEDBACK_INTENT_CLASSES } from "./overlayClasses";

const INTENTS = ["information", "success", "attention", "critical"];
const ICONS = { information: "i", success: "✓", attention: "!", critical: "!" };

// Clic principal sans touche de modification : Ctrl/Cmd/Maj+clic garde le geste natif du lien.
const isPlainPrimaryClick = (event) => !(
  event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
  || (event.button !== undefined && event.button !== 0)
);

export function InlineFeedback({
  actionHref = null,
  actionLabel = null,
  children = null,
  description = null,
  intent = "information",
  onAction = null,
  title,
}) {
  useThemeA("InlineFeedback");
  assertOneOf("InlineFeedback", "intent", intent, INTENTS);
  assertNonEmptyString("InlineFeedback", "title", title);
  if (actionLabel && typeof onAction !== "function" && !actionHref) {
    throw new Error(
      "[WUI_INVALID_CONTRACT] InlineFeedback.onAction or actionHref is required when actionLabel is set."
    );
  }
  const role = intent === "critical" ? "alert" : "status";
  // Contenu détaillé (liste, action secondaire) sous la description. Avant ce slot,
  // les enfants passés au composant étaient ignorés en silence par plusieurs vues.
  const hasChildren = React.Children.toArray(children).length > 0;

  return (
    <aside
      className={`akv:animate-[ak-rise_var(--ak-v2-motion-entrance)_var(--ak-v2-motion-easing)_both] akv:motion-reduce:animate-none akv:box-border akv:grid akv:grid-cols-[auto_minmax(0,1fr)_auto] akv:items-center akv:gap-3 akv:rounded-ak-control akv:border-0 akv:bg-ak-subtle akv:px-4 akv:py-3 akv:max-[640px]:grid-cols-[auto_minmax(0,1fr)] ${FEEDBACK_INTENT_CLASSES[intent]}`}
      role={role}
    >
      <span className="akv:inline-flex akv:h-6 akv:w-6 akv:items-center akv:justify-center akv:rounded-full akv:border-0 akv:bg-ak-surface/70 akv:font-ak-body akv:text-xs akv:font-extrabold akv:text-ak-text" aria-hidden="true">{ICONS[intent]}</span>
      <div className="akv:flex akv:min-w-0 akv:flex-col akv:gap-1">
        <strong className="akv:font-ak-body akv:text-sm akv:leading-[1.35] akv:text-ak-text">{title}</strong>
        {description && <span className="akv:font-ak-body akv:text-[0.8125rem] akv:leading-[1.45] akv:text-ak-muted">{description}</span>}
        {hasChildren && (
          <div className="akv:font-ak-body akv:text-[0.8125rem] akv:leading-[1.45] akv:text-ak-muted" data-ak-v2-slot="details">
            {children}
          </div>
        )}
      </div>
      {actionLabel && (
        // Sur téléphone, l'action passe sous le texte sur toute la largeur : laissée
        // dans la colonne de l'icône, elle l'élargissait et écrasait le texte.
        <span className="akv:max-[640px]:col-span-2 akv:max-[640px]:justify-self-start">
          {actionHref ? (
            // Action qui mène à une autre page : vrai lien (Ctrl+clic, nouvel
            // onglet), navigation de l'application sur un clic simple (règle 10).
            <ButtonLink
              href={actionHref}
              intent="quiet"
              onClick={(event) => {
                if (typeof onAction !== "function" || !isPlainPrimaryClick(event)) return;
                event.preventDefault();
                onAction(event);
              }}
            >
              {actionLabel}
            </ButtonLink>
          ) : (
            <Button intent="quiet" onClick={onAction}>{actionLabel}</Button>
          )}
        </span>
      )}
    </aside>
  );
}

InlineFeedback.propTypes = {
  actionHref: PropTypes.string,
  actionLabel: PropTypes.string,
  children: PropTypes.node,
  description: PropTypes.string,
  intent: PropTypes.oneOf(INTENTS),
  onAction: PropTypes.func,
  title: PropTypes.string.isRequired,
};
